import { KeyValue } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
  WritableSignal,
} from '@angular/core';
import { DropdownEvent, DropdownItem } from '@shared/abstraction';
import { TransactionType } from '@shared/helpers/enums/transaction-type';
import { ODataFilter } from '@shared/modules/grid/abstractions';
import { ExternalFilterComponent } from '@shared/modules/prime-grid-filters/components/base-components/external-filter.component';
import { FilterTypes } from '@shared/modules/prime-grid-filters/helpers/enums/filter-type.enum';
import { Filter } from '@shared/modules/prime-grid-filters/helpers/types/filter.type';
import { ColumnFilterState } from '@shared/modules/prime-grid-filters/models/column-filter-state';
import { transactionTypeItems } from 'src/app/modules/admin/constants/transaction-type-items';

@Component({
  standalone: false,
  selector: 'gstar-transaction-type-dropdown',
  templateUrl: './transaction-type-dropdown.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransactionTypeDropdownComponent extends ExternalFilterComponent<DropdownItem> {
  transactionType = input<TransactionType | string | number | undefined>();

  updateTransactionType = output<DropdownItem>();

  enumTransactionTypeItems: typeof TransactionType = TransactionType;
  selectedTransactionType = signal<DropdownItem>(transactionTypeItems[0]);
  transactionTypeItems: WritableSignal<DropdownItem[]> =
    signal<DropdownItem[]>(transactionTypeItems);

  constructor() {
    super();

    effect(() => {
      const transactionTypeItem = this.findTransactionTypeItem(
        this.transactionType()
      );

      if (transactionTypeItem) {
        this.selectedTransactionType.set(transactionTypeItem);
      }
    });

    this.updateFilter.subscribe((state: ColumnFilterState<DropdownItem>) => {
      const value = state.getValue<DropdownItem>();
      this.selectedTransactionType.set(value);
    });
  }

  override columnFilterDefinition(): Filter {
    return {
      type: FilterTypes.Dropdown,
      oDataPath: 'transactionType/transactionTypeId',
      oDataQueryBuilder: (columnFilter: ODataFilter): void => {
        this.updateTransactionTypeHandler(columnFilter);
      },
      options: {
        items: transactionTypeItems.map(item => {
          return <KeyValue<string, string>>{
            value: item.value,
            key: item.label,
          };
        }),
      },
    };
  }

  onSelectItem(event: DropdownEvent): void {
    const selectedValue = this.utils.parseTo<DropdownItem>(event.selected);
    this.updateTransactionType.emit(selectedValue);
    this.updateExternalValue(selectedValue);
  }

  private updateTransactionTypeHandler(columnFilter: ODataFilter): void {
    const selectedValue = columnFilter.getValue<KeyValue<string, string>>();
    if (selectedValue) {
      columnFilter.updateQuery({
        in: selectedValue.value,
      });
    }

    this.updateSession(<ColumnFilterState<DropdownItem>>columnFilter);
  }

  private findTransactionTypeItem(
    transactionType: TransactionType | string | number | undefined
  ): DropdownItem | undefined {
    if (transactionType === undefined || transactionType === null) {
      return undefined;
    }

    const transactionTypeValue = Number(transactionType);

    if (Number.isNaN(transactionTypeValue)) {
      return undefined;
    }

    return transactionTypeItems.find(
      item => Number(item.value) === transactionTypeValue
    );
  }
}
